/**
 * Catálogo de misiones del juego. Datos puros: solo el guion (diálogos,
 * decisiones, exploraciones, recompensas). NADA de motor aquí — el motor
 * vive en `src/engine/` y consume este catálogo vía `getMission(id)`.
 *
 * Misión 1 — "La casa random" — es la misión de la beta (canon Adri
 * 2026-07-11, ver `docs/biblia/misiones.md`). El resto del catálogo
 * llegará en bloques posteriores cuando se validen más anécdotas.
 *
 * Reglas duras del estilo (derivan del GDD y de `biblia/personajes.md`):
 * - Apodos en MAYÚSCULAS como en la biblia. SOLO apodos, nunca nombres
 *   reales.
 * - Cada personaje mantiene su forma de hablar: MOAI insulta con cariño,
 *   BANAGRAYN dice pocas palabras (y lo que dice es gracioso), GERARDBU
 *   va directo sin rodeos.
 * - Frases cortas, castellano de barrio, humor seco. Sin narrador
 *   omnisciente — todo se cuenta con diálogo entre los colegas.
 * - Sin emojis dentro del texto de las líneas (los emojis viven en los
 *   retratos y en los `ObjetoExplorable.emoji`).
 */

import type { Mission } from './types';

/** Catálogo de misiones. De momento solo la Misión 1 de la beta. */
export const misiones: Mission[] = [
  {
    id: 'la-casa-random',
    titulo: 'La casa random',
    tipo: 'principal',
    personajesIds: ['sdowp', 'banagrayn', 'moai', 'gerardbu'],
    locationId: 'el-monte',
    umbralRespetoDesbloqueo: 0,
    // Sin requiereFlags: la beta arranca con esta misión disponible.
    steps: [
      // ─── Paso 1 (índice 0): REUNIÓN en la casa — diálogo ───────────
      // Fumando de calentada alrededor de la mesa. Banter natural del
      // grupo, se decide ir a inspeccionar la casa random del monte.
      {
        tipo: 'dialogo',
        lineas: [
          {
            personajeId: 'moai',
            emocion: 'neutral',
            texto: 'Buenas, hijos de puta. Acabo de fichar a las seis, estoy reventado del camión.',
          },
          {
            personajeId: 'sdowp',
            emocion: 'neutral',
            texto: 'Hombre, Moai. Siéntate, que queda sitio. Enciéndete un pitillo.',
          },
          {
            personajeId: 'moai',
            emocion: 'neutral',
            texto: 'Anda ya, bro, déjate de tanta amabilidad, que me pongo malo.',
          },
          {
            personajeId: 'banagrayn',
            emocion: 'neutral',
            texto: 'Tío, trae parcas.',
          },
          {
            personajeId: 'moai',
            emocion: 'neutral',
            texto: 'Qué me vas a dar tú, si es que ni hablar quieres, cabrón.',
          },
          {
            personajeId: 'gerardbu',
            emocion: 'neutral',
            texto: 'Bueno, ¿qué? Yo digo de bajar al monte a ver la casa random.',
          },
          {
            personajeId: 'sdowp',
            emocion: 'neutral',
            texto: '¿La del camino de la izquierda? La del otro día, la que vimos de pasada.',
          },
          {
            personajeId: 'banagrayn',
            emocion: 'neutral',
            texto: 'Esa. Hace meses que no pisa nadie por allí.',
          },
          {
            personajeId: 'gerardbu',
            emocion: 'neutral',
            texto: 'Pues vamos, que luego se nos echa la noche encima.',
          },
          {
            personajeId: 'moai',
            emocion: 'neutral',
            texto: 'Venga, panda de vagos, moveos ya, pesados.',
          },
        ],
      },

      // ─── Paso 2 (índice 1): REUNIÓN — decisión de sabor ───────────
      // Sin impacto narrativo: ambas opciones llevan al siguiente paso
      // (CAMINO diálogo, índice 2). Cada opción da +1 respeto como
      // micro-recompensa (canon: "decisiones sin impacto, sabor").
      {
        tipo: 'decision',
        pregunta: '¿Por dónde tiramos? ¿Pista forestal o el atajo de las zarzas?',
        opciones: [
          {
            texto: 'Por la pista forestal. Más tranquilo, vamos con tiempo.',
            setFlags: ['camino-pista'],
            siguientePasoIndex: 2,
            respeto: 1,
          },
          {
            texto: 'Por el atajo de las zarzas. Vamos justos, así llegamos antes.',
            setFlags: ['camino-atajo'],
            siguientePasoIndex: 2,
            respeto: 1,
          },
        ],
      },

      // ─── Paso 3 (índice 2): CAMINO por el monte — diálogo ─────────
      // Picándose entre ellos. Mención canon a HASBEEN KICKED (adivina
      // dónde están solo con una foto).
      {
        tipo: 'dialogo',
        lineas: [
          {
            personajeId: 'gerardbu',
            emocion: 'neutral',
            texto: 'Venga, a ver quién llega el primero a aquella rama de ahí.',
          },
          {
            personajeId: 'moai',
            emocion: 'neutral',
            texto: 'Tío, llevo ocho horas en el camión. Como me hagas correr te reviento, ¿estamos?',
          },
          {
            personajeId: 'banagrayn',
            emocion: 'neutral',
            texto: 'Si echo una foto desde aquí, el Hasbeen Kicked adivina dónde estamos.',
          },
          {
            personajeId: 'moai',
            emocion: 'neutral',
            texto: 'Ese tío es un puto friki, bro. Lo clava con una sola foto.',
          },
          {
            personajeId: 'gerardbu',
            emocion: 'neutral',
            texto: 'Seguro que hasta la casa exacta nos dice, el cabrón.',
          },
          {
            personajeId: 'banagrayn',
            emocion: 'neutral',
            texto: 'Seguro.',
          },
        ],
      },

      // ─── Paso 4 (índice 3): CAMINO — decisión de sabor ────────────
      // Apuesta tonta sobre qué habrá en la casa. Sin impacto narrativo:
      // ambas opciones llevan a EXPLORACIÓN (índice 4).
      {
        tipo: 'decision',
        pregunta: '¿Qué apostáis a que hay dentro de la casa?',
        opciones: [
          {
            texto: 'Yo digo que hay una moto con las llaves puestas.',
            setFlags: ['apuesta-ktm'],
            siguientePasoIndex: 4,
            respeto: 1,
          },
          {
            texto: 'Yo digo que hay un generador. Si lo hay, me lo llevo.',
            setFlags: ['apuesta-generador'],
            siguientePasoIndex: 4,
            respeto: 1,
          },
        ],
      },

      // ─── Paso 5 (índice 4): EXPLORACIÓN en la casa random ─────────
      // Objetos CANON (KTM, generador, Winston+OCB, caja herramientas)
      // más relleno de la biblia. `riesgo` lo usará la misión en el
      // regreso (no el motor).
      {
        tipo: 'exploracion',
        descripcion: 'La casa random. Ni un alma. La puerta, abierta.',
        objetos: [
          {
            id: 'ktm-125',
            nombre: 'KTM 125 to flama',
            emoji: '🏍️',
            descripcion:
              'Una KTM 125 tuneada ahí tirada. Y lo mejor de todo: CON LAS LLAVES PUESTAS.',
            dinero: 120,
            riesgo: 60,
          },
          {
            id: 'generador',
            nombre: 'Generador',
            emoji: '⚡',
            descripcion:
              'Un generador eléctrico medio polvoriento. Cuando lo enchufas, ruge.',
            dinero: 60,
            riesgo: 25,
          },
          {
            id: 'winston-ocb',
            nombre: 'Winston + papel OCB',
            emoji: '🚬',
            descripcion: 'Un paquete de Winston sin abrir y papel OCB al lado.',
            dinero: 10,
            riesgo: 5,
          },
          {
            id: 'caja-herramientas',
            nombre: 'Caja de herramientas',
            emoji: '🧰',
            descripcion: 'Una caja de herramientas con cosas útiles dentro.',
            dinero: 40,
            riesgo: 15,
          },
          {
            id: 'garrafa',
            nombre: 'Garrafa de gasolina medio llena',
            emoji: '⛽',
            descripcion: 'Una garrafa de gasolina por la mitad. Apesta que flipas.',
            dinero: 15,
            riesgo: 10,
          },
          {
            id: 'radio-vieja',
            nombre: 'Radio vieja que aún funciona',
            emoji: '📻',
            descripcion: 'Una radio de los noventa que todavía pita.',
            dinero: 12,
            riesgo: 5,
          },
          {
            id: 'silla-camping',
            nombre: 'Silla de camping coja',
            emoji: '🪑',
            descripcion: 'Una silla de camping con una pata doblada.',
            dinero: 2,
            riesgo: 2,
          },
          {
            id: 'calendario-2009',
            nombre: 'Calendario de taller de 2009',
            emoji: '📅',
            descripcion: 'Un calendario de un taller mecánico de hace mil años.',
            dinero: 0,
            respeto: 1,
            riesgo: 0,
          },
        ],
      },

      // ─── Paso 6 (índice 5): REGRESO a la casa — diálogo ───────────
      // Tensión cómica: susto con un vecino/perro a lo lejos que queda en
      // nada. Humor nervioso. Mención canon a CHANCLEX (ingeniero F1)
      // porque se llevan la KTM.
      {
        tipo: 'dialogo',
        lineas: [
          {
            personajeId: 'gerardbu',
            emocion: 'neutral',
            texto: 'Venga, bajemos ya. Como nos vean con esto estamos jodidos.',
          },
          {
            personajeId: 'banagrayn',
            emocion: 'neutral',
            texto: 'Por ahí hay un perro ladrando. ¿Veis algo?',
          },
          {
            personajeId: 'moai',
            emocion: 'neutral',
            texto: 'Calla, bro, me cago en todo. Me ha dado un vuelco el corazón.',
          },
          {
            personajeId: 'gerardbu',
            emocion: 'neutral',
            texto: 'Tranquilo, era un perro. Venga, deprisa y en silencio.',
          },
          {
            personajeId: 'banagrayn',
            emocion: 'neutral',
            texto: 'Si la KTM no arranca, esto Chanclex lo afina en dos minutos.',
          },
          {
            personajeId: 'moai',
            emocion: 'neutral',
            texto: 'Con el botín que llevamos nos da para una cena de las gordas, bro. Ande, ande.',
          },
        ],
      },

      // ─── Paso 7 (índice 6): CIERRE — diálogo en la casa ───────────
      // Satisfacción, moai insultando de alegría. Termina diciendo que
      // toca bajar al barrio a dormir.
      {
        tipo: 'dialogo',
        lineas: [
          {
            personajeId: 'gerardbu',
            emocion: 'neutral',
            texto: 'Ya estamos. Se puede respirar tranquilo.',
          },
          {
            personajeId: 'moai',
            emocion: 'neutral',
            texto: 'Hijos de puta, ha salido redondo esto.',
          },
          {
            personajeId: 'banagrayn',
            emocion: 'neutral',
            texto: 'Buena noche.',
          },
          {
            personajeId: 'sdowp',
            emocion: 'neutral',
            texto: 'Venga, pues ahora al barrio. Toca bajar y dormir.',
          },
          {
            personajeId: 'moai',
            emocion: 'neutral',
            texto: 'Anda ya, pesados, dejad de tocar las narices y vámonos ya.',
          },
        ],
      },

      // ─── Paso 8 (índice 7): RECOMPENSA de la misión ───────────────
      // Respeto + dinero FIJOS de la misión. El botín de los objetos
      // EXPLORABLES (dinero/respeto por objeto recogido) se suma aparte
      // desde el motor, no aquí.
      {
        tipo: 'recompensa',
        respeto: 10,
        dinero: 20,
      },
    ],
  },
];

/**
 * Devuelve la Mission con ese id. Lanza error claro si no existe (o si
 * el catálogo está vacío) para que la escena muestre un panel de error
 * legible en vez de fallar en silencio.
 */
export function getMission(id: string): Mission {
  const m = misiones.find((mis) => mis.id === id);
  if (!m) {
    const disponibles = misiones.map((mis) => mis.id).join(', ');
    const sufijo =
      disponibles.length > 0
        ? ` Ids disponibles: ${disponibles}`
        : ' El catálogo está vacío: el contenido real llega en otro bloque.';
    throw new Error(`[misiones] Misión con id "${id}" no encontrada.${sufijo}`);
  }
  return m;
}
